/**
 * FARESONEN — der en hel container slettes, og det eneste stedet i Tvitvi der man må SKRIVE noe
 * for å få lov (#28, #28b).
 *
 * =================================================================================================
 * REGELEN DEN HÅNDHEVER, ORDRETT FRA #28b:
 *
 *   > Små objekter slettes med søppelkasse-ikon og Angre-toast. Store containere (org, festival,
 *   > årgang) slettes via faresone og skriv-navnet-bekreftelse.
 *
 * De to halvdelene er ikke smak. En kontaktrad kan gjenopprettes ved å opprette den på nytt med
 * samme e-post, så «Angre» kan mene det den sier — DESIGN.md §11.4 forbyr da å spørre. En festival
 * har ingen slik vei tilbake: årganger, bookinger, advancer, oppgaver og dokumenter henger under
 * den, og en toast som forsvinner etter åtte sekunder er ikke et tilbud man kan ta imot på et
 * produksjonsmøte. Derfor er dette spørsmålet ærlig, og derfor koster det et navn å svare på.
 *
 * =================================================================================================
 * HVORFOR NAVNET, OG IKKE «ER DU SIKKER?».
 *
 * «Er du sikker?» besvares med musen, av refleks, av den samme hånden som nettopp bommet. Å skrive
 * «God Nok Festivalen» kan ikke gjøres ved et uhell, og — dette er den viktigste halvdelen — det er
 * det ENESTE som skiller «slett denne festivalen» fra «slett den over den i lista». Feilen dette
 * verner mot er ikke at noen ikke visste at det var farlig; det er at noen slettet feil rad.
 *
 * =================================================================================================
 * DET ER ET STEG, IKKE EN DIALOG OPPÅ EN DIALOG (K-7).
 *
 * «Modal over modal: ALDRI. Et nytt steg åpnes inne i samme flate (panel eller steg), eller
 * erstatter den.» Faresonen står nederst i et skjema som allerede ER en modal, så bekreftelsen kan
 * ikke være en `Modal` til. `DeleteContainerStep` er derfor bare en KROPP: kalleren bytter ut
 * innholdet i dialogen den allerede har åpen, bytter tittelen og bytter knapperaden. Det gir også
 * den riktige Escape-oppførselen gratis — ett lag, én tast, én lukking.
 *
 * `namesMatch` ligger ved siden av kroppen fordi porten er kallerens: det er kallerens knapp som
 * skal være av til navnet stemmer, og en komponent som eide både feltet og knappen ville vært en
 * modal igjen.
 *
 * =================================================================================================
 * DEN VET INGENTING OM FESTIVALER. Både festivalen og årgangen bruker den, og setningene er
 * kallerens — «hva som forsvinner» er ikke det samme for de to. Formen er ikke kallerens, og det er
 * hele grunnen til at fila finnes: to faresoner som ser ulike ut er to regler å huske.
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { errorKey } from '@/api/errors';
import { Button } from '@/components/ui/Button';
import { Callout } from '@/components/ui/Callout';
import { Field } from '@/components/ui/Field';
import { TextField } from '@/components/ui/TextField';

export interface DangerZoneProps {
  /** One sentence saying what this zone is for, in the caller's own words. */
  description: string;
  /** The red control. The caller switches its own surface to the step below. */
  actionLabel: string;
  onAction: () => void;
  'data-testid'?: string;
  actionTestId?: string;
}

/**
 * The band at the foot of an edit form.
 *
 * ONE LINE OF SEPARATION, NOT THREE. DESIGN.md §5: a card with a border and a shadow and a
 * background is three ways of saying the same thing. Glass 2 took the red outline too ("kant betyr
 * inndatafelt", G6): the divider separates it, the red title and the red word on the button carry
 * the danger, and the filled red button belongs to the confirmation step this opens (§16.5.1).
 */
export function DangerZone({
  description,
  actionLabel,
  onAction,
  'data-testid': testId = 'danger-zone',
  actionTestId,
}: DangerZoneProps): ReactNode {
  const { t } = useTranslation();

  return (
    <section data-testid={testId} className="mt-4 flex flex-col gap-2 border-t border-default pt-3">
      <h3 className="text-sm font-medium text-danger">{t('dangerZone.title')}</h3>
      <p className="max-w-prose text-sm leading-snug text-muted">{description}</p>
      <div>
        <Button
          variant="quietDanger"
          onClick={onAction}
          {...(actionTestId === undefined ? {} : { 'data-testid': actionTestId })}
        >
          {actionLabel}
        </Button>
      </div>
    </section>
  );
}

/**
 * Is what was typed the name that was asked for?
 *
 * TRIMMED AND CASE-INSENSITIVE, and that is not a weakening of the gate. The protection is «which
 * row is this», and «god nok festivalen» answers that as precisely as «God Nok Festivalen» does.
 * Refusing on a capital letter would only teach people to copy and paste the name, which is the
 * one way of answering that skips the reading.
 */
export function namesMatch(typed: string, required: string): boolean {
  return typed.trim().toLocaleLowerCase() === required.trim().toLocaleLowerCase();
}

export interface DeleteContainerStepProps {
  /** What is about to happen, in one sentence. */
  intro: string;
  /** Everything that goes with it. One line each, so it can be read rather than parsed. */
  consequences: readonly string[];
  /** The exact text that has to be typed. The name, never an id. */
  requiredName: string;
  /** Controlled by the caller, because the caller's footer button is gated on it. */
  typed: string;
  onTypedChange: (next: string) => void;
  /** The failure from the mutation, rendered in the step rather than behind it. */
  error?: unknown;
  'data-testid'?: string;
  inputTestId?: string;
}

/**
 * The confirmation, as the body of the dialog that is already open: what disappears, and the name
 * that has to be written out.
 */
export function DeleteContainerStep({
  intro,
  consequences,
  requiredName,
  typed,
  onTypedChange,
  error,
  'data-testid': testId = 'delete-container-step',
  inputTestId,
}: DeleteContainerStepProps): ReactNode {
  const { t } = useTranslation();
  const listId = useId();

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {error === undefined || error === null ? null : (
        <Callout tone="danger">{t(errorKey(error))}</Callout>
      )}

      <p className="max-w-prose text-sm leading-snug text-default">{intro}</p>

      {/*
        WHAT GOES WITH IT, AS A LIST. #28 asks the confirmation to show what is deleted —
        «årganger, bookinger og advancer, oppgaver, dokumenter» — and a comma-separated sentence of
        five nouns is a sentence the eye slides off at exactly the moment it must not.
      */}
      <div className="flex flex-col gap-1">
        <p id={listId} className="text-sm font-medium text-default">
          {t('dangerZone.takesWithIt')}
        </p>
        <ul
          aria-labelledby={listId}
          className="flex flex-col gap-0.5 ps-4"
          data-testid={`${testId}-consequences`}
        >
          {consequences.map((line) => (
            <li key={line} className="list-disc text-sm leading-snug text-muted">
              {line}
            </li>
          ))}
        </ul>
      </div>

      <Field
        label={t('dangerZone.confirmLabel', { name: requiredName })}
        description={t('dangerZone.confirmHelp')}
      >
        {(control) => (
          <TextField
            {...control}
            value={typed}
            onChange={(event) => onTypedChange(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            {...(inputTestId === undefined ? {} : { 'data-testid': inputTestId })}
          />
        )}
      </Field>
    </div>
  );
}
